import { useShallow } from "zustand/react/shallow";
import type { StateCreator } from "zustand";
import { supabase } from "@/lib/supabase";
import type {
  SocialLink,
  SocialLinkInsert,
  SocialLinkUpdate,
} from "@/types/database";
import { getErrorMessage } from "@/lib/errors/messages";
import { logError } from "@/lib/errors/handler";
import {
  createComplexStore,
  createCrudSlice,
  type CrudEntityStore,
} from "./factory/complex";
import { persistOrderedUpdates } from "./factory/persistOrderedUpdates";

const MAX_SOCIAL_LINKS = 8;

interface SocialLinksState extends CrudEntityStore<SocialLink> {
  socialLinks: SocialLink[];
  fetchSocialLinks: () => Promise<void>;
  createSocialLink: (link: SocialLinkInsert) => Promise<void>;
  updateSocialLink: (id: string, updates: SocialLinkUpdate) => Promise<void>;
  deleteSocialLink: (id: string) => Promise<void>;
  reorderSocialLinks: (newOrder: SocialLink[]) => Promise<void>;
}

const createSocialLinksDomain: StateCreator<SocialLinksState> = (set, get) =>
  ({
    socialLinks: [],
    fetchSocialLinks: async () => {
      await get().fetchItems();
      set({ socialLinks: get().items });
    },

    createSocialLink: async (link: SocialLinkInsert) => {
      set({ isLoading: true, error: null });
      try {
        const currentLinks = get().socialLinks;
        if (currentLinks.length >= MAX_SOCIAL_LINKS) {
          throw new Error(`Maximum ${MAX_SOCIAL_LINKS} social links allowed`);
        }
        const position = currentLinks.length
          ? Math.max(...currentLinks.map((item) => item.position)) + 1
          : 0;
        const { data, error } = await supabase
          .from("social_links")
          .insert({ ...link, position })
          .select()
          .single();
        if (error) throw error;
        const created = data as SocialLink;
        set((state) => ({
          socialLinks: [...state.socialLinks, created],
          items: [...state.items, created],
        }));
      } catch (error: unknown) {
        logError("createSocialLink", error);
        set({ error: getErrorMessage(error) });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    updateSocialLink: async (id: string, updates: SocialLinkUpdate) => {
      set({ isLoading: true, error: null });
      try {
        const { data, error } = await supabase
          .from("social_links")
          .update(updates)
          .eq("id", id)
          .select()
          .single();
        if (error) throw error;
        const updated = data as SocialLink;
        set((state) => ({
          socialLinks: state.socialLinks.map((link) =>
            link.id === id ? updated : link,
          ),
          items: state.items.map((link) => (link.id === id ? updated : link)),
        }));
      } catch (error: unknown) {
        logError("updateSocialLink", error);
        set({ error: getErrorMessage(error) });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    deleteSocialLink: async (id: string) => {
      set({ isLoading: true, error: null });
      try {
        const { error } = await supabase
          .from("social_links")
          .delete()
          .eq("id", id);
        if (error) throw error;
        set((state) => ({
          socialLinks: state.socialLinks.filter((link) => link.id !== id),
          items: state.items.filter((link) => link.id !== id),
        }));
      } catch (error: unknown) {
        logError("deleteSocialLink", error);
        set({ error: getErrorMessage(error) });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    reorderSocialLinks: async (newOrder: SocialLink[]) => {
      const oldLinks = get().socialLinks;
      const ordered = newOrder.map((link, position) => ({ ...link, position }));
      set({ socialLinks: ordered, items: ordered, error: null });
      try {
        await persistOrderedUpdates(ordered, (link) =>
          supabase
            .from("social_links")
            .update({ position: link.position })
            .eq("id", link.id),
        );
      } catch (error: unknown) {
        logError("reorderSocialLinks", error);
        set({
          socialLinks: oldLinks,
          items: oldLinks,
          error: getErrorMessage(error),
        });
      }
    },
  }) as unknown as SocialLinksState;

export const useSocialLinksStore = createComplexStore<SocialLinksState>(
  createCrudSlice<SocialLink>("social_links", "position"),
  createSocialLinksDomain,
);

export const useSocialLinks = () =>
  useSocialLinksStore((state) => state.socialLinks);
export const usePublishedSocialLinks = () =>
  useSocialLinksStore(
    useShallow((state) =>
      (state.socialLinks ?? []).filter((link) => link.is_published),
    ),
  );
export const useFetchSocialLinks = () =>
  useSocialLinksStore((state) => state.fetchSocialLinks);
export const useCreateSocialLink = () =>
  useSocialLinksStore((state) => state.createSocialLink);
export const useUpdateSocialLink = () =>
  useSocialLinksStore((state) => state.updateSocialLink);
export const useDeleteSocialLink = () =>
  useSocialLinksStore((state) => state.deleteSocialLink);
export const useReorderSocialLinks = () =>
  useSocialLinksStore((state) => state.reorderSocialLinks);

export default useSocialLinksStore;
